// EstimateRoomsEditor
import React, { useEffect, useMemo, useRef, useState } from "react";
import { calcRoomsSummary } from "../../utils/calcRoomsSummary";
import RoomsSummary from "./Rooms/RoomsSummary";
import RoomCard from "./Rooms/RoomCard";
import {
    normalizeRooms,
    normalizeRoom,
    normalizeWall,
    makeOpening,
    makePoint,
    makeCeilingLight,
    num,
    setAt,
    removeAt,
    moveAt,
} from "../../helpers/rooms.helpers";

export default function EstimateRoomsEditor({ value, onChange }) {
    const valueSig = useMemo(() => JSON.stringify(Array.isArray(value) ? value : []), [value]);

    const [rooms, setRooms] = useState(() => normalizeRooms(Array.isArray(value) ? value : []));

    useEffect(() => {
        setRooms(normalizeRooms(Array.isArray(value) ? value : []));
    }, [valueSig]);

    const computed = useMemo(() => calcRoomsSummary(rooms), [rooms]);

    const [openRoomIds, setOpenRoomIds] = useState([]);
    const [dragRoomId, setDragRoomId] = useState(null);
    const [dragOverRoomId, setDragOverRoomId] = useState(null);

    const initRef = useRef(false);

    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;
        setOpenRoomIds(rooms.map((r) => r.id));
    }, [rooms]);

    useEffect(() => {
        const ids = rooms.map((r) => r.id);
        setOpenRoomIds((prev) => prev.filter((id) => ids.includes(id)));
    }, [rooms]);

    const isRoomOpen = (roomId) => openRoomIds.includes(roomId);

    const toggleRoom = (roomId) => {
        setOpenRoomIds((prev) => (prev.includes(roomId) ? prev.filter((x) => x !== roomId) : [...prev, roomId]));
    };

    const TRACE = true;

    const commit = (nextRoomsLike) => {
        const nextRooms = normalizeRooms(nextRoomsLike);
        setRooms(nextRooms);

        const nextComputed = calcRoomsSummary(nextRooms);

        if (TRACE) {
            const r0 = nextRooms?.[0] || {};
            console.log("[ROOMS][commit] rooms.len =", nextRooms.length);
            console.log("[ROOMS][commit] r0.name =", r0?.name);
            console.log("[ROOMS][commit] r0.ceilingType =", r0?.ceilingType);
            console.log("[ROOMS][commit] r0.ceilingDropMm =", r0?.ceilingDropMm);
            console.log("[ROOMS][commit] r0.ceilingAreaM2Override =", r0?.ceilingAreaM2Override);
            console.log("[ROOMS][commit] r0.ceilingLights =", Array.isArray(r0?.ceilingLights) ? r0.ceilingLights : r0?.ceilingLights);
        }

        if (typeof onChange === "function") onChange(nextRooms, nextComputed?.totals);
    };


    const addRoom = () => {
        const nextRoom = normalizeRoom({}, rooms.length);
        setOpenRoomIds((prev) => (prev.includes(nextRoom.id) ? prev : [...prev, nextRoom.id]));
        commit([...rooms, nextRoom]);
    };

    const removeRoomByIndex = (ri) => {
        const id = rooms?.[ri]?.id;
        if (id) setOpenRoomIds((prev) => prev.filter((x) => x !== id));
        commit(removeAt(rooms, ri));
    };

    const updateRoom = (ri, patch) => {
        const next = setAt(rooms, ri, { ...rooms[ri], ...patch });
        commit(next);
    };

    const setRoomShape = (ri, shape) => {
        const r = rooms[ri];
        const nextShape = shape === "polygon" ? "polygon" : "rectangle";
        updateRoom(ri, { ...r, shape: nextShape });
    };

    const applyRectToWalls = (ri) => {
        const r = rooms[ri];
        const L = num(r?.rect?.lengthMm);
        const W = num(r?.rect?.widthMm);
        const walls = Array.isArray(r.walls) ? r.walls : [];
        if (walls.length < 4) return;

        const nextWalls = walls.map((w, i) => {
            const isL = i === 0 || i === 2;
            const isW = i === 1 || i === 3;
            return { ...w, lengthMm: isL ? L : isW ? W : w.lengthMm };
        });

        updateRoom(ri, { walls: nextWalls });
    };

    const addWall = (ri) => {
        const r = rooms[ri];
        if (r.shape !== "polygon") return;
        const walls = Array.isArray(r.walls) ? r.walls : [];
        const nextWalls = [...walls, normalizeWall({}, ri, walls.length)];
        updateRoom(ri, { walls: nextWalls });
    };

    const removeWall = (ri, wi) => {
        const r = rooms[ri];
        if (r.shape !== "polygon") return;
        const walls = Array.isArray(r.walls) ? r.walls : [];
        updateRoom(ri, { walls: removeAt(walls, wi) });
    };

    const updateWall = (ri, wi, patch) => {
        const r = rooms[ri];
        const walls = Array.isArray(r.walls) ? r.walls : [];
        const nextWalls = setAt(walls, wi, { ...walls[wi], ...patch });
        updateRoom(ri, { walls: nextWalls });
    };

    const addDoor = (ri, wi, init) => {
        const w = rooms[ri]?.walls?.[wi];
        const doors = Array.isArray(w?.doors) ? w.doors : [];
        const nextDoor = { ...makeOpening(), ...(init || {}) };
        updateWall(ri, wi, { doors: [...doors, nextDoor] });
        return nextDoor.id;
    };

    const addWindow = (ri, wi) => {
        const w = rooms[ri]?.walls?.[wi];
        const windows = Array.isArray(w?.windows) ? w.windows : [];
        updateWall(ri, wi, { windows: [...windows, makeOpening()] });
    };

    const updateOpening = (ri, wi, kind, oi, patch) => {
        const w = rooms[ri]?.walls?.[wi];
        const arr = Array.isArray(w?.[kind]) ? w[kind] : [];
        const next = setAt(arr, oi, { ...arr[oi], ...patch });
        updateWall(ri, wi, { [kind]: next });
    };

    const removeOpening = (ri, wi, kind, oi) => {
        const w = rooms[ri]?.walls?.[wi];
        const arr = Array.isArray(w?.[kind]) ? w[kind] : [];
        updateWall(ri, wi, { [kind]: removeAt(arr, oi) });
    };

    const addPoint = (ri, wi, kind) => {
        const w = rooms[ri]?.walls?.[wi];
        const arr = Array.isArray(w?.[kind]) ? w[kind] : [];
        updateWall(ri, wi, { [kind]: [...arr, makePoint()] });
    };

    const updatePoint = (ri, wi, kind, pi, patch) => {
        const w = rooms[ri]?.walls?.[wi];
        const arr = Array.isArray(w?.[kind]) ? w[kind] : [];
        const next = setAt(arr, pi, { ...arr[pi], ...patch });
        updateWall(ri, wi, { [kind]: next });
    };

    const removePoint = (ri, wi, kind, pi) => {
        const w = rooms[ri]?.walls?.[wi];
        const arr = Array.isArray(w?.[kind]) ? w[kind] : [];
        updateWall(ri, wi, { [kind]: removeAt(arr, pi) });
    };

    const addCeilingLight = (ri) => {
        const r = rooms[ri];
        const arr = Array.isArray(r?.ceilingLights) ? r.ceilingLights : [];
        updateRoom(ri, { ceilingLights: [...arr, makeCeilingLight()] });
    };

    const updateCeilingLight = (ri, li, patch) => {
        const r = rooms[ri];
        const arr = Array.isArray(r?.ceilingLights) ? r.ceilingLights : [];
        const next = setAt(arr, li, { ...arr[li], ...patch });
        updateRoom(ri, { ceilingLights: next });
    };

    const removeCeilingLight = (ri, li) => {
        const r = rooms[ri];
        const arr = Array.isArray(r?.ceilingLights) ? r.ceilingLights : [];
        updateRoom(ri, { ceilingLights: removeAt(arr, li) });
    };

    const updateOpeningsBatch = (updates) => {
        const list = Array.isArray(updates) ? updates : [];
        if (!list.length) return;

        const nextRooms = rooms.slice();

        const roomCache = {};
        const wallCache = {};
        const arrCache = {};

        const ensureRoom = (ri) => {
            if (!Number.isFinite(ri) || !nextRooms[ri]) return null;
            if (!roomCache[ri]) {
                const r0 = nextRooms[ri];
                const walls0 = Array.isArray(r0?.walls) ? r0.walls : [];
                const r1 = { ...r0, walls: walls0.slice() };
                nextRooms[ri] = r1;
                roomCache[ri] = r1;
            }
            return roomCache[ri];
        };

        const ensureWall = (ri, wi) => {
            const r = ensureRoom(ri);
            if (!r) return null;
            const walls = r.walls;
            if (!Number.isFinite(wi) || !walls[wi]) return null;
            const k = `${ri}:${wi}`;
            if (!wallCache[k]) {
                const w0 = walls[wi];
                const w1 = { ...w0 };
                walls[wi] = w1;
                wallCache[k] = w1;
            }
            return wallCache[k];
        };

        const ensureArr = (ri, wi, kind) => {
            const w = ensureWall(ri, wi);
            if (!w) return null;
            const k = `${ri}:${wi}:${kind}`;
            if (!arrCache[k]) {
                const a0 = Array.isArray(w?.[kind]) ? w[kind] : [];
                const a1 = a0.slice();
                w[kind] = a1;
                arrCache[k] = a1;
            }
            return arrCache[k];
        };

        list.forEach((u) => {
            const ri = Number(u?.ri);
            const wi = Number(u?.wi);
            const oi = Number(u?.oi);
            const kind = String(u?.kind || "");
            const patch = u?.patch || {};
            if (!kind) return;
            const arr = ensureArr(ri, wi, kind);
            if (!arr || !arr[oi]) return;
            arr[oi] = { ...arr[oi], ...patch };
        });

        commit(nextRooms);
    };

    const removeOpeningsBatch = (removals) => {
        const list = Array.isArray(removals) ? removals : [];
        if (!list.length) return;

        const nextRooms = rooms.slice();

        const roomCache = {};
        const wallCache = {};
        const arrCache = {};

        const ensureRoom = (ri) => {
            if (!Number.isFinite(ri) || !nextRooms[ri]) return null;
            if (!roomCache[ri]) {
                const r0 = nextRooms[ri];
                const walls0 = Array.isArray(r0?.walls) ? r0.walls : [];
                const r1 = { ...r0, walls: walls0.slice() };
                nextRooms[ri] = r1;
                roomCache[ri] = r1;
            }
            return roomCache[ri];
        };

        const ensureWall = (ri, wi) => {
            const r = ensureRoom(ri);
            if (!r) return null;
            const walls = r.walls;
            if (!Number.isFinite(wi) || !walls[wi]) return null;
            const k = `${ri}:${wi}`;
            if (!wallCache[k]) {
                const w0 = walls[wi];
                const w1 = { ...w0 };
                walls[wi] = w1;
                wallCache[k] = w1;
            }
            return wallCache[k];
        };

        const ensureArr = (ri, wi, kind) => {
            const w = ensureWall(ri, wi);
            if (!w) return null;
            const k = `${ri}:${wi}:${kind}`;
            if (!arrCache[k]) {
                const a0 = Array.isArray(w?.[kind]) ? w[kind] : [];
                const a1 = a0.slice();
                w[kind] = a1;
                arrCache[k] = a1;
            }
            return arrCache[k];
        };

        const groups = {};
        list.forEach((r) => {
            const ri = Number(r?.ri);
            const wi = Number(r?.wi);
            const oi = Number(r?.oi);
            const kind = String(r?.kind || "");
            if (!Number.isFinite(ri) || !Number.isFinite(wi) || !Number.isFinite(oi) || !kind) return;
            const k = `${ri}:${wi}:${kind}`;
            if (!groups[k]) groups[k] = [];
            groups[k].push(oi);
        });

        Object.keys(groups).forEach((k) => {
            const [riS, wiS, kind] = k.split(":");
            const ri = Number(riS);
            const wi = Number(wiS);
            const idxs = (groups[k] || []).slice().sort((a, b) => b - a);

            const arr = ensureArr(ri, wi, kind);
            if (!arr) return;

            idxs.forEach((oi) => {
                if (oi >= 0 && oi < arr.length) arr.splice(oi, 1);
            });
        });

        commit(nextRooms);
    };

    const moveRoomById = (fromId, toId) => {
        const from = rooms.findIndex((r) => r.id === fromId);
        const to = rooms.findIndex((r) => r.id === toId);
        if (from < 0 || to < 0 || from === to) return;
        commit(moveAt(rooms, from, to));
    };

    const onDragStartRoom = (roomId, e) => {
        setDragRoomId(roomId);
        setDragOverRoomId(null);
        if (e?.dataTransfer) {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", roomId);
        }
    };

    const onDragEndRoom = () => {
        setDragRoomId(null);
        setDragOverRoomId(null);
    };

    const onDragOverRoom = (roomId, e) => {
        if (e) e.preventDefault();
        setDragOverRoomId(roomId);
        if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    };

    const onDropRoom = (roomId, e) => {
        if (e) e.preventDefault();
        const id = dragRoomId || (e?.dataTransfer ? e.dataTransfer.getData("text/plain") : null);
        setDragRoomId(null);
        setDragOverRoomId(null);
        if (!id) return;
        moveRoomById(id, roomId);
    };

    const moveUp = (ri) => {
        if (ri <= 0) return;
        commit(moveAt(rooms, ri, ri - 1));
    };

    const moveDown = (ri) => {
        if (ri >= rooms.length - 1) return;
        commit(moveAt(rooms, ri, ri + 1));
    };

    const actions = {
        updateRoom,
        setRoomShape,
        applyRectToWalls,
        addWall,
        removeWall,
        updateWall,
        addDoor,
        addWindow,
        updateOpening,
        removeOpening,
        addPoint,
        updatePoint,
        removePoint,
        addCeilingLight,
        updateCeilingLight,
        removeCeilingLight,
        updateOpeningsBatch,
        removeOpeningsBatch,
    };

    return (
        <div className="smeta-rooms">
            <div className="smeta-rooms__head">
                <div className="smeta-rooms__head-title">Комнаты / Стены / Окна / Двери / Электрика</div>
                <button type="button" className="smeta-rooms__btn smeta-rooms__btn--primary" onClick={addRoom}>
                    + Добавить комнату
                </button>
            </div>

            <RoomsSummary totals={computed?.totals} />

            {rooms.map((room, ri) => {
                const roomComputed = computed?.rooms?.[ri];
                return (
                    <RoomCard
                        key={room.id || ri}
                        room={room}
                        roomsAll={rooms}
                        ri={ri}
                        roomComputed={roomComputed}
                        open={isRoomOpen(room.id)}
                        dragOver={dragOverRoomId === room.id}
                        onToggle={() => toggleRoom(room.id)}
                        onRemove={() => removeRoomByIndex(ri)}
                        onMoveUp={() => moveUp(ri)}
                        onMoveDown={() => moveDown(ri)}
                        onDragStart={onDragStartRoom}
                        onDragEnd={onDragEndRoom}
                        onDragOver={onDragOverRoom}
                        onDrop={onDropRoom}
                        actions={actions}
                    />
                );
            })}
        </div>
    );
}
